import { useRef } from 'react'
import type { KeyboardEvent, ReactNode } from 'react'
import { ChevronDown, Copy } from 'lucide-react'
import {
  detailHero,
  detailHeroAside,
  detailHeroDescription,
  detailHeroEyebrow,
  detailHeroValue,
  drawerSection,
  drawerSectionDescription,
  drawerSectionHead,
  drawerSectionTitle,
  keyValueGrid,
  keyValueItem,
  keyValueLabel,
  keyValueValue,
  technicalContent,
  technicalCopyButton,
  technicalDetails,
  technicalRow,
  technicalSummary,
  technicalValue,
  tabButton,
  tabList,
} from './DrawerLayout.css'

export type DrawerTab = {
  id: string
  label: string
}

export type DrawerTabsProps = {
  tabs: DrawerTab[]
  value: string
  onChange: (value: string) => void
  ariaLabel: string
}

export function DrawerTabs({ tabs, value, onChange, ariaLabel }: DrawerTabsProps) {
  const refs = useRef<Array<HTMLButtonElement | null>>([])

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
      return
    }

    event.preventDefault()
    let nextIndex = index
    if (event.key === 'Home') nextIndex = 0
    if (event.key === 'End') nextIndex = tabs.length - 1
    if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length
    if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length
    const next = tabs[nextIndex]
    if (!next) return
    onChange(next.id)
    refs.current[nextIndex]?.focus()
  }

  return (
    <div className={tabList} role="tablist" aria-label={ariaLabel}>
      {tabs.map((tab, index) => {
        const selected = tab.id === value
        return (
          <button
            ref={(node) => {
              refs.current[index] = node
            }}
            type="button"
            className={tabButton}
            role="tab"
            id={`drawer-tab-${tab.id}`}
            aria-selected={selected}
            aria-controls={`drawer-panel-${tab.id}`}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(tab.id)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            key={tab.id}
          >
            {tab.label}
          </button>
        )
      })}
    </div>
  )
}

export function DrawerTabPanel({
  id,
  active,
  children,
}: {
  id: string
  active: boolean
  children: ReactNode
}) {
  if (!active) return null
  return (
    <div
      role="tabpanel"
      id={`drawer-panel-${id}`}
      aria-labelledby={`drawer-tab-${id}`}
      tabIndex={0}
    >
      {children}
    </div>
  )
}

export function DrawerSection({
  title,
  description,
  action,
  children,
}: {
  title: ReactNode
  description?: ReactNode
  action?: ReactNode
  children: ReactNode
}) {
  return (
    <section className={drawerSection}>
      <div className={drawerSectionHead}>
        <div>
          <h3 className={drawerSectionTitle}>{title}</h3>
          {description ? <p className={drawerSectionDescription}>{description}</p> : null}
        </div>
        {action}
      </div>
      {children}
    </section>
  )
}

export function DetailHero({
  eyebrow,
  value,
  description,
  aside,
  tone = 'neutral',
}: {
  eyebrow?: ReactNode
  value: ReactNode
  description?: ReactNode
  aside?: ReactNode
  tone?: 'neutral' | 'success' | 'warning' | 'danger' | 'informative'
}) {
  return (
    <section className={detailHero} data-tone={tone}>
      <div>
        {eyebrow ? <div className={detailHeroEyebrow}>{eyebrow}</div> : null}
        <div className={detailHeroValue}>{value}</div>
        {description ? <p className={detailHeroDescription}>{description}</p> : null}
      </div>
      {aside ? <div className={detailHeroAside}>{aside}</div> : null}
    </section>
  )
}

export function KeyValueGrid({
  items,
  columns = 2,
}: {
  items: Array<{ label: ReactNode; value: ReactNode }>
  columns?: 1 | 2
}) {
  return (
    <dl className={keyValueGrid} data-columns={columns}>
      {items.map((item, index) => (
        <div className={keyValueItem} key={index}>
          <dt className={keyValueLabel}>{item.label}</dt>
          <dd className={keyValueValue}>{item.value}</dd>
        </div>
      ))}
    </dl>
  )
}

export function TechnicalDetails({
  title = 'Detalhes técnicos',
  rows,
  defaultOpen = false,
}: {
  title?: ReactNode
  rows: Array<{ label: ReactNode; value: string; copyable?: boolean }>
  defaultOpen?: boolean
}) {
  return (
    <details className={technicalDetails} open={defaultOpen}>
      <summary className={technicalSummary}>
        <span>{title}</span>
        <ChevronDown size={17} aria-hidden="true" />
      </summary>
      <div className={technicalContent}>
        {rows.map((row, index) => (
          <div className={technicalRow} key={index}>
            <span>{row.label}</span>
            <code className={technicalValue} title={row.value} tabIndex={0}>
              {row.value}
            </code>
            {row.copyable !== false ? (
              <button
                type="button"
                className={technicalCopyButton}
                aria-label={`Copiar ${typeof row.label === 'string' ? row.label : 'valor técnico'}`}
                onClick={() => {
                  if (navigator.clipboard) {
                    void navigator.clipboard.writeText(row.value)
                  }
                }}
              >
                <Copy size={16} aria-hidden="true" />
              </button>
            ) : null}
          </div>
        ))}
      </div>
    </details>
  )
}
